import type {RestResponse} from '../api/request'
import type {AppStorage} from '../index'

export const BASIC_RADIUS = 25

export type Unknown = null | undefined
export type AdvertiseHash = string

export const emptyLocation: LocationModel = {longitude: 0, latitude: 0}
export const idStringKeyExtractor = (item: {id: string}) => item.id
export const hashKeyExtractor = (item: AdvertiseListItemModel) => item.hash
export const defaultLanguage: LanguageModel = {isoCode: 'en'}

export type AdvertiseBottomSheetModalType = 'context' | 'contact' | null

export const ALL_CATEGORIES_ID = 2009
export const ADVERTISE_CALL_AVAILABILITY_TIME_FORMAT = 'YYYY-MM-DD HH:mm Z'
export type ProductCondition = 'new' | 'used'

export enum ChangeParameterType {
  phone,
  email,
  password,
}

export enum AdvertiseDetailTabs {
  photo,
  price,
  location,
  characteristics,
  description,
  notes,
  seller,
  similarAds,
}

export enum RestResponseCodes {
  userNotFound = 'USER_NOT_FOUND',
  errorTimeout = 'ERR_TIMEOUT',
}

export enum CoverLinkType {
  shareAdvert = 'share_ad',
  shareUserProfile = 'share_self_profile',
  shareOtherProfile = 'share_user_profile',
}

export interface ImageResponse {
  readonly hash: string
  readonly url: string
}

export interface AdvertiseNote {
  readonly note: string
  readonly date: number
}

export interface DegradationType {
  readonly value: number
  readonly radius: number
  readonly title: string
  readonly key: TypeOfDegradation
}

export const DEGRADATION_TYPE_ABSENT: DegradationType = {
  value: 1,
  radius: 0,
  title: 'HIGH_ACCURACY',
  key: 'absent',
}
export const DEGRADATION_TYPE_LOW: DegradationType = {
  value: 2,
  radius: 1,
  title: 'MEDIUM_ACCURATE',
  key: 'low',
}
export const DEGRADATION_TYPE_HIGH: DegradationType = {
  value: 3,
  radius: 2,
  title: 'LOW_ACCURATE',
  key: 'high',
}

export const degradations: Array<DegradationType> = [
  DEGRADATION_TYPE_ABSENT,
  DEGRADATION_TYPE_LOW,
  DEGRADATION_TYPE_HIGH,
]

export type TypeOfDegradation = 'absent' | 'low' | 'high'

export const defaultUSCountry: CountryModel = {
  id: '-1',
  isoCode: 'US',
  title: 'Соединённые Штаты Америки',
  phoneLength: 10,
  phoneMask: 'XXX-XXX-XXXX',
  phonePrefix: '1',
}

export const WeeDays = {
  '1': 'SUNDAY',
  '2': 'MONDAY',
  '3': 'TUESDAY',
  '4': 'WEDNESDAY',
  '5': 'THURSDAY',
  '6': 'FRIDAY',
  '7': 'SATURDAY',
}

export const sortTypes: Array<SortModel> = [
  {
    type: 'DEFAULT',
    key: '',
    direction: 'asc',
  },
  {
    type: 'BY_NOVELTY',
    key: 'date_updated',
    direction: 'asc',
  },
  {
    direction: 'desc',
    type: 'SORT_DIRECTION_MESSAGE_DATE_DESC',
    key: 'date_updated',
  },
  {
    direction: 'asc',
    type: 'SORT_DIRECTION_MESSAGE_DISTANCE_ASC',
    key: 'distance',
  },
  {
    direction: 'desc',
    type: 'SORT_DIRECTION_MESSAGE_DISTANCE_DESC',
    key: 'distance',
  },
  {
    direction: 'asc',
    type: 'SORT_BY_PRICE_LOW_TO_HIGH',
    key: 'price',
  },
  {
    direction: 'desc',
    type: 'SORT_BY_PRICE_HIGH_TO_LOW',
    key: 'price',
  },
]

export const ascSort: (l: Sortable, r: Sortable) => number = (l, r) => {
  return l.sort > r.sort ? 1 : -1
}

export interface Sortable {
  readonly sort: number
}

export interface CurrencyModel {
  readonly symbol: string
  readonly code: string
}

export interface FetchCurrencyModelResponse {
  readonly currency: CurrencyModel[]
}

export interface AdvertiseLocationModel {
  readonly city: string
  readonly description: string
  readonly latitude: number
  readonly longitude: number
  readonly distance: string | Unknown
}

export interface CommunicationType {
  readonly name: string
  readonly logo: string | Unknown
  readonly value: string
}

export interface AdvertiseOwnerModel {
  readonly languages: Array<string>
  readonly hash: string
  readonly name: string
  readonly dateRegistered: number
  readonly image: string | Unknown
  readonly companyLogo: string | Unknown
  readonly phoneNum: string | Unknown
  readonly isOnline: boolean
  readonly isVerified: boolean // убрать
  readonly isSubscribed: boolean
  readonly ratingMark: number
  readonly ratingMarkCnt: number
  readonly advertsActiveCount: number
  readonly userType: 'simple'
}

export type AdvertiseExtraFieldType = 'text' | 'url'
export interface AdvertiseExtraField {
  readonly type: AdvertiseExtraFieldType
  readonly value: string
  // this field is present when type is text
  readonly text?: {
    readonly value: string
    readonly unitCode: string | Unknown
  }
  // this field is present when type is url
  readonly icon?: string
}

export interface AdvertiseListResponse {
  readonly data: Array<AdvertiseListItemModel>
  readonly aggregatedFields: Array<CACategoryDataFieldModel>
  readonly rootCategoryId: number
}

export type AdvertiseState =
  | 'draft'
  | 'blocked'
  | 'blockedPermanently'
  | 'active'
  | 'sold'
  | 'archived'
  | 'deleted'

export interface AdvertiseTranslatable {
  titleOriginal: string
  descriptionOriginal: string
  title: string
  description: string
  isTranslated?: boolean
}

export interface AdvertiseListItemModel extends AdvertiseTranslatable {
  readonly hash: string
  readonly langCode: string
  readonly url: string
  readonly categoryId: number
  readonly address?: string
  state: AdvertiseState
  readonly onModeration: boolean
  readonly showRefreshButton: boolean
  readonly extraFields: AdvertiseExtraField[] | Unknown
  readonly rootCategoryId: number
  readonly metroStation?: MetroStationListItemModel

  readonly price: string
  readonly oldPrice: string
  readonly discount: {isPriceDown: boolean; value: string} | null
  // unix time
  readonly dateUpdated: number | Unknown

  isFavorite: boolean
  viewed: boolean
  readonly views: number
  readonly owner: {
    hash: string
    companyLogo: string | Unknown
    communicationTypes: CommunicationType[]
  }
  readonly location: AdvertiseLocationModel
  readonly images: string[]
  readonly videos: string[]
  readonly videosExtended: {uri: string; thumbnail: string}[]
  readonly secureDeal: boolean
  readonly hasVideo: boolean
  readonly has360View: boolean
  readonly isTop: boolean
  readonly isVip: boolean
  readonly isExclusive: boolean
  readonly showCallButton: boolean
  readonly daysBeforeArchive: number | null
  readonly withDelivery?: boolean
}

export interface CheckPhonePermissions {
  readonly num: string
  readonly callAvailableTime: string
  readonly displayAllowed: boolean
}

export interface AdvertiseDetail {
  readonly advert: AdvertiseFullModel
  readonly owner: AdvertiseOwnerModel
}

export interface Banner {
  readonly id: string
  readonly image: string
  readonly key: string
  readonly specific: string // Big title
  readonly title: string
  readonly description: string
  /** color HEX */
  readonly theme: string
  /**
   * | {
   *     readonly type: 'web'
   *     readonly redirect: string
   *   }
   * | {
   *     readonly type: 'category'
   *     readonly categoryId: string
   *   }
   * | {
   *     readonly type: 'categoryOptions'
   *     readonly categoryId: string
   *     readonly priseMax: string
   *   }
   * | {
   *     readonly type: 'newAd'
   *   }
   * */
  readonly action: string
}

export interface Banners {
  readonly banners: Banner[]
  readonly total: number
}

export interface AdvertiseBlockReason {
  //utc
  readonly dateBlocked: number
  readonly reasonCode: string
  readonly reasonDescription: string
}

export interface MetroStationModel {
  lineColor: string
  stationName: string
  distance: string
}

export type MetroStationListItemModel = Omit<MetroStationModel, 'distance'>

export interface AdvertiseFullModel extends AdvertiseListItemModel {
  /** In format {@link ADVERTISE_CALL_AVAILABILITY_TIME_FORMAT} */
  readonly callAvailabilityTime: string
  readonly dateSold: number | null
  readonly condition: ProductCondition
  readonly priceMeter: string | Unknown

  readonly favoriteCounter: number
  readonly isCallEnabled: boolean
  readonly showCallButton: boolean
  readonly showRefreshButton: boolean
  readonly degradation: TypeOfDegradation
  readonly shortUrl: string
  readonly fields: FieldDTO[]
  isPriceSubscribed: boolean
  readonly bargainPossible: boolean
  readonly swapPossible: boolean
  readonly block?: AdvertiseBlockReason
  readonly metroStations?: MetroStationModel[]
}

export interface ComplainReasonModel {
  id: string
  title_code: string
  have_child: string
  subReasons: ComplainReasonModel[]
}

// export interface CategoryMultiselectItem extends ListDialogItem {
//   readonly title: string
//   readonly id: string
//   readonly sort: number
//   readonly icon?: string
// }

// export interface CategoryField {
//   readonly id: string
//   readonly type: string
//   readonly isFilterable: boolean
//   readonly title: string
//   readonly isRequired: boolean
//   readonly slug: string
//   readonly sort: number
//   readonly maxLength: number
//   readonly items: Array<CategoryMultiselectItem>
// }

export interface SortModel {
  type: string
  key: string
  direction: string
}

export enum FilterPublication {
  WITHIN_24_HOURS = 'WITHIN_24_HOURS',
  WITHIN_7_DAYS = 'WITHIN_7_DAYS',
  ALL_TIME = 'ALL_TIME',
}

export interface LocationModel {
  readonly latitude: number
  readonly longitude: number
}

export interface CountryModel {
  readonly id: string
  readonly isoCode: string
  readonly phonePrefix: string
  readonly title: string
  readonly phoneLength: number
  readonly phoneMask: string

  readonly value?: string
  readonly key?: string
  readonly has_adverts?: string
}

export interface AuthUserResponse {
  readonly hash: string
  readonly promo: string
  readonly uid?: string
}

export enum Gender {
  UNDEFINED = 3,
  MALE = 1,
  FEMALE = 2,
}

export const GenderNames: {[key in Gender]: string} = {
  [Gender.UNDEFINED]: 'UNDEFINED',
  [Gender.MALE]: 'MALE',
  [Gender.FEMALE]: 'FEMALE',
}

export interface AddressModel {
  address_text: string | Unknown
  country: string | Unknown
  lat: string | Unknown
  lng: string | Unknown
}

export type PushNotificationTypeV1 =
  | 'subscribeads_notification'
  | 'message_notification'
  | 'myadv_notification'
  | 'newads_by_filter_notification'
  | 'favorite_notification'
  | 'requests_notification'
  | 'changeprice_ads_notification'
  | 'info_push_notification'

export const PushNotificationTypeV2ToV1Map: {
  [key in PushNotificationType]: {
    title: string
    description: string
    v1Key: PushNotificationTypeV1
  }
} = {
  subscribeProduct: {
    description: 'SUBSCRIBEADS_NOTIFICATION',
    title: 'SUBSCRIBEADS_NOTIFICATION_TITLE',
    v1Key: 'subscribeads_notification',
  },
  message: {
    description: 'MESSAGE_NOTIFICATION',
    title: 'MESSAGE_NOTIFICATION_TITLE',
    v1Key: 'message_notification',
  },
  myProduct: {
    description: 'MYADV_NOTIFICATION',
    title: 'MYADV_NOTIFICATION_TITLE',
    v1Key: 'myadv_notification',
  },
  newFilterProd: {
    description: 'NEW_ADS_BY_FILTER_NOTIFICATION',
    title: 'NEW_ADS_BY_FILTER_NOTIFICATION_TITLE',
    v1Key: 'newads_by_filter_notification',
  },
  newFavoriteProd: {
    description: 'FAVORITE_NOTIFICATION',
    title: 'FAVORITE_NOTIFICATION_TITLE',
    v1Key: 'favorite_notification',
  },
  request: {
    description: 'INFO_PUSH_REQUEST',
    title: 'INFO_PUSH_REQUEST_TITLE',
    v1Key: 'requests_notification',
  },
  changePrice: {
    description: 'CHANGEPRICE_ADS_NOTIFICATION',
    title: 'CHANGEPRICE_ADS_NOTIFICATION_TITLE',
    v1Key: 'changeprice_ads_notification',
  },
  infoPush: {
    description: 'INFO_PUSH_NOTIFICATION',
    title: 'INFO_PUSH_NOTIFICATION_TITLE',
    v1Key: 'info_push_notification',
  },
}

export interface NotificationModel {
  pushType: PushNotificationType
  title: string
  description: string
  enable: boolean
  v1Key: string
}

export type PushNotificationType =
  | 'subscribeProduct'
  | 'message'
  | 'myProduct'
  | 'newFilterProd'
  | 'newFavoriteProd'
  | 'request'
  | 'changePrice'
  | 'infoPush'

export type NotificationSettingsItems = {
  [key in PushNotificationType]: boolean
}

export interface SchedulePeriodModel {
  from: string
  to: string
}

export interface ScheduleDayModel {
  day: number
  active: boolean
  periods: Array<SchedulePeriodModel>
}

export interface UserPersonalSettingsModel {
  name: string
  surname: string
  sex: Gender
  address?: AddressModel
  email: string
  emailConfirmed: boolean
  telegram?: string
  whatsapp?: string
  viber?: string
  skype?: string
  phoneNum?: string
  typeAccount: RegistrationType
  degradationType: number
  notificationItems: NotificationSettingsItems
  callScheduleList: Array<ScheduleDayModel>
}

export interface UserLocationModel extends LocationModel {
  description: string
  city: string
  country?: CountryModel
}

export interface UserSettingsModel {
  shouldDisplayPhone: boolean
  personal?: UserPersonalSettingsModel
  location?: {
    description?: string
    city?: string
    country?: string
  }
}

export interface LanguageModel {
  isoCode: string
}

export interface SettingsLanguageModel {
  id: number
  code: string
  name: string
  // translated name on code language
  displayName?: string
}

export interface UserModel {
  id: string
  imageUrl?: string
  name: string
  surname: string
  isOnline: boolean
  isBlocked: boolean
  isShop: boolean
  isSubscribed: boolean
  rating: number
  ratingCount: number
  activeProductCount: number
  soldProductCount: number
  archiveProductCount: number
}

export interface OwnerModel {
  hash: string
  name: string
  surname?: string
  imageUrl?: string
  social?: string
  isOnline: boolean
  registrationDate: number
  subscribers: number
  subscribs: number
  rating: number
  ratingCount: number
  activeProductCount: number
  soldProductCount: number
  archiveProductCount: number
  draftsProductCount: number
  isSubscribed: boolean
  settings: UserSettingsModel
  mainLanguage: LanguageModel
  additionalLanguages?: Array<LanguageModel>
  bonusCount: number
  moneyCount: number
  newRegistration: boolean
}

export enum AuthType {
  phone = 1,
  email = 2,
  facebook = 3,
  google = 5,
  apple = 7,
}

export enum SettingsOperation {
  avatar = 1,
  phone,
  contact,
  address,
  userLanguages,
  translateLanguages,
  degradation,
  settingsCalls,
  links,
  organization,
  blackList,
  notifications,
  changeEmail,
  loadAvatar,
  changePassword,
}

export enum RegistrationType {
  individual = 1,
  shop,
}

export interface PredictionModel {
  description: string
  placeId: string
}

export interface PredictionsModel extends Array<PredictionModel> {}

export interface PlaceInfoResponseDTO {
  formatted_address: string
  geometry: {
    location: {
      lat: number
      lng: number
    }
    viewport: {
      northeast: {
        lat: number
        lng: number
      }
      southwest: {
        lat: number
        lng: number
      }
    }
  }
}
export interface PlaceInfoModel {
  address: string
  location: LocationModel
}

export enum UserProfileType {
  owner,
  guest,
}

export enum SubscriptionOperationType {
  subscriptions = 1,
  subscribers = 2,
}

export interface ReviewModel {
  id: string
  adCreatedAt: number
  adId: string
  adTitle: string
  comment: string | Unknown
  commentOriginal: string | Unknown
  userRating: number
  userImage?: string
  userId: string
  userName: string
  userRegistrationDate: number
  isUserOnline: boolean
}

export interface GeoPositionItemModel {
  id: string
  code: string
  word: string
  slug?: string
  readonly value: string
  readonly key: string
  has_adverts?: string
}

export interface GeoPositionModel {
  readonly country?: GeoPositionItemModel
  readonly region?: GeoPositionItemModel
  readonly city?: GeoPositionItemModel
}

export interface FieldDTO {
  fieldId: number
  fieldNameText: string
  fieldValueText: string[]
  fieldType: FieldTypeModel
}

export interface ToggleSubscribePriceRequestDTO {
  hash: string
  secur: string
  type: number
  hash_ad: string
}

export interface UserStatsModel {
  bonusAccount: number
  activeCount: number
  soldCount: number
  archiveCount: number
  draftCount: number
  reviewCount: number
  onModerationCount: number
}

export interface ToggleSubscribePricePayload {
  type: ToggleSubscribePriceRequestDTO['type']
  hash_ad: ToggleSubscribePriceRequestDTO['hash_ad']
}

export interface FetchAdvertisesPayload {
  limit?: number
  page?: number
  filter: Filter
  onlyAggregation?: boolean
  advHash?: string
  cacheId?: string
}

export interface RestFetchUserProductsPayload {
  userId: string
  page: number
  cacheId?: string
}

export type RestFetchUserProducts = (
  payload: RestFetchUserProductsPayload,
) => Promise<RestResponse<Array<AdvertiseListItemModel>>>

export interface ComplainAdvertiseRequestDTO {
  hash: string
  secur: string
  get_list?: boolean
  hash_ad?: string
  complain_id?: number
  comment?: string
}

export type RemoveFromSaleType = 'soldAdverto' | 'soldOther' | 'changedMind'

export interface ComplainAdvertisePayload {
  get_list?: ComplainAdvertiseRequestDTO['get_list']
  hash_ad: ComplainAdvertiseRequestDTO['hash_ad']
  complain_id?: ComplainAdvertiseRequestDTO['complain_id']
  comment?: ComplainAdvertiseRequestDTO['comment']
}

type IconModel = {url: string}
type CategoryIcon = {
  icon: IconModel
  url: {and: string; ios: string; ios_color: string; web_svg: string}
}

type MapDisplayTypeModel = 'collapsed'
type ScreenTypeModel = 'simple' | 'array'
export type FieldTypeModel =
  | 'multiselect'
  | 'int'
  | 'select'
  | 'string'
  | 'checkbox'
  | 'iconselect'
  | 'title'

export interface CreateAdvertiseCategoryExtraModel {
  readonly allowVideo: boolean
  readonly allowFree: boolean
  readonly enabled360View: boolean
  readonly minPhotos: number
  readonly allowSecureDeal: boolean
  readonly mapDisplayType: MapDisplayTypeModel
  readonly allowUsed: boolean
  readonly ageRestricted: boolean
}

export interface CategoryExtra {
  readonly color: string
  readonly colorBack: string
}

export interface CACategoryModel {
  readonly id: number
  readonly name: string
  readonly slug: string
  readonly icon: CategoryIcon | Unknown
  readonly parentId: number
  readonly extras: CreateAdvertiseCategoryExtraModel
  readonly items: Array<CACategoryModel>
  readonly extra?: CategoryExtra
}

export interface AppCategoryModel {
  readonly name: string
  readonly id: number
  readonly icon: string | Unknown
  readonly parentId: number
  readonly extras: CreateAdvertiseCategoryExtraModel
  readonly hasChildren: boolean
  readonly path: string
  readonly placementIndex: number
  readonly extraColor?: string
  readonly extraColorBack?: string
}

export interface CACategoryDataModel {
  readonly id: number
  readonly icon: CategoryIcon | Unknown
  readonly isProduct: boolean
  readonly allowVideo: boolean
  readonly titleAutogenerated: boolean
  readonly screenType: ScreenTypeModel
  readonly allowFree: boolean
  readonly descriptionLengthMax: number
  readonly enabled360View: boolean
  readonly minPhotos: number
  readonly maxPhotos: number
  readonly allowSecureDeal: boolean
  readonly maxVideoDuration: number
  readonly mapDisplayType: MapDisplayTypeModel
  readonly allowUsed: boolean
  readonly ageRestricted: boolean
  readonly name: string
  //TODO
  readonly minPrice?: number
  //TODO
  readonly tileImages: []
  fields: Array<CACategoryDataFieldModel>
}

export interface CACategoryDataFieldModel {
  id: number
  fieldType: FieldTypeModel
  isFilterable: boolean
  isFillingRequired: boolean
  maxLength: number
  //TODO
  minValue: any | null
  //TODO
  maxValue: any | null
  icon: CategoryIcon | Unknown
  itemType: 'simple' | 'title'
  name: string
  slug: string
  description: string | null
  page: any | null
  pageSort: any | null
  relatedFields: CACategoryDataFieldModel[]
  multiselects: MultiselectFieldModel
  popularMultiselects: CommonMultiselectFieldModel[]
  arrayTypeFields: any[]
  dependenceSequenceId: number | Unknown
  _dependenceSequenceId?: number | Unknown
  // assign this field for later use after you receive it from method `fetchCatalogFieldDependent`
  dependenceSequence: Array<number> | Unknown
  nextUiDependenceFieldId: number | Unknown
}

export type MultiSelectID = CommonMultiselectFieldModel['id']
export interface CommonMultiselectFieldModel {
  id: number
  fieldId: number
  count: number
  icon: string
  isOther: boolean
  itemType: 'simple' | 'title'
  value: string
  isPopular: boolean | null
  hasDependents: boolean
  isVisible: boolean
  readonly parentId: number | Unknown
}

export interface MultiselectFieldModel extends CommonMultiselectFieldModel {
  top: MultiselectFieldModel[]
  other: MultiselectFieldModel[]
}

export interface CAMedia {
  url: string
  progress: number
  uploadId?: string
  hash?: string
  error?: string
  type: 'image' | 'video'
}

export interface CAParamContent {
  langCode: string
  title: string
  description: string
  errorTitle?: string
  errorDescription?: string
}

interface CreateAdvertiseContentModel {
  langCode: string
  title: string
  description?: string
}

export interface DependentField {
  readonly fieldId: number
  fieldValue?: any
  otherWasSelected: boolean
  readonly seqId: number
}

export interface DependentFieldRequestParams {
  readonly dependenceSequenceId: number
  readonly dependenceSequence: any[]
  readonly otherValueWasSelected: boolean
}

export type FieldsModel = Record<CACategoryDataFieldModel['id'], any[]>
export type ArrayTypeFields = Record<
  CACategoryDataFieldModel['id'],
  Record<string, any[]>
>
export interface CAParamsModel {
  hash: string | null
  userHash: string | null
  categoryId: number
  photos: CAMedia[]
  videos: CAMedia[]
  fields: FieldsModel
  arrayTypeFields: ArrayTypeFields
  isFastSale: boolean
  price: string
  isFree: boolean
  currency?: CurrencyModel
  content: Array<CAParamContent>
  location: LocationModel
  addressDraft: string
  degradation: TypeOfDegradation
  isSecureDeal: boolean
  condition: string | null
  isBargainPossible: boolean
  isSwapPossible: boolean
  isExclusive: boolean
  sellerPaidDelivery: boolean
  appId: string
  currencies: CurrencyModel[]
  dependence?: DependentField[]

  data?: CACategoryDataModel
  isVip: boolean
  isTop: boolean
}

export interface CreateAdvertiseModel {
  categoryId: number
  userHash: string
  content: CreateAdvertiseContentModel[]
  price: string
  currency: string
  fields: FieldsModel
  photos: Array<ImageResponse>
  videos: Array<ImageResponse>
  location: LocationModel
  condition: string | null
  isBargainPossible: boolean
  isSwapPossible: boolean
  isExclusive: boolean
  isVip: boolean
  isTop: boolean
  isFastSale: boolean
  isSecureDeal: boolean
  has360View: boolean
  degradation: string
  dependenceSequenceId: number | undefined
}

export const defaultFilter = (storage: AppStorage): Filter => ({
  onlyFromSubscribed: false,
  search: '',
  fieldValues: new Map<CACategoryDataFieldModel['id'], any[]>(),
  sort: sortTypes[0],
  location: storage.location,
  distanceMax: storage.searchRadius ?? BASIC_RADIUS,
  locationAddress: storage.searchAddress ?? undefined,
  secureDeal: false,
  onlyDiscounted: false,
  published: FilterPublication.ALL_TIME,
  priceMin: null,
  priceMax: null,
})

export function filterKeyId(
  value: CACategoryDataFieldModel,
): CACategoryDataFieldModel['id'] {
  return value.id
}

export interface FilterNotificationSettings {
  pushNotification: boolean
  emailNotification: boolean
  partOfDay: number
}

export interface Filter {
  categoryId?: number
  cityId?: number
  condition?: ProductCondition
  countryId?: number
  distanceMax?: number
  fieldValues: Map<CACategoryDataFieldModel['id'], any[]>
  id?: string
  location?: LocationModel
  locationAddress?: string
  notification?: FilterNotificationSettings
  onlyDiscounted: boolean
  onlyFromSubscribed: boolean
  priceMax: string | Unknown
  priceMin: string | Unknown
  published: FilterPublication
  regionId?: number
  search: string
  secureDeal: boolean
  sort: SortModel
  title?: string
  withPhoto?: boolean
}

export interface FilterListItem {
  category?: AppCategoryModel
  readonly filter: Filter
}

export interface CatalogFieldDependentResponse {
  readonly nextField: CACategoryDataFieldModel
  readonly dependenceSequenceId: number
  readonly dependenceSequence: Array<number>
  readonly otherValueWasSelected: boolean
}

export interface DraftModel {
  readonly hash: string
  readonly advertDraft: CAParamsModel & {categoryName?: string}
  //utc
  readonly updateDate: number
}

export interface UploadImageResponse {
  items: ImageResponse[]
}

interface Price {
  price: number
  days: number
}

interface PaidService {
  paidService: string
  currencyId: number
  prices: Price[]
}

export interface ServiceDataResponse {
  requestId: string
  data: ServiceData
}

export interface ServiceData {
  autoUp: PaidService
  hot: PaidService
  top: PaidService
  vip: PaidService
}
